/**
 * The worklist's company window, embedded in a customer file.
 *
 * Same account of the relationship the board opens in its own window — how long
 * they have been waiting, how long since anyone spoke to them, the work still
 * pending, the work already closed (folded, fetched on demand) and the full
 * history — brought into the companies and customers screens.
 *
 * Deliberately a thin host around the board's own popup body rather than a
 * second implementation: the screens must show the same rows, with the same
 * status, language and classification tags, or they drift apart the first time
 * one of them changes. The block comes from GET /v2/sales-inbox/worklist/open,
 * assembled by the very code the board uses.
 *
 * Scope: pass companySecId for a company file — every contact of the company is
 * covered — or customerSecId for one customer, whether that customer belongs to
 * a company or not. The two are exclusive; see stripCompany() for why.
 */
define('salesInboxWorklistEmbedView',[
  'module',
  'app',
  'marionette',
  'underscore',
  'entities/sales_inbox'
], function (Module, App, Marionette, _) {
  'use strict';

  Module.exports = Marionette.LayoutView.extend({
    className: 'wl-embed-view',

    template: _.template(
      '<div class="wl-embed-state js-state">Loading…</div>' +
      '<div class="wl-embed-host js-host"></div>'
    ),

    regions: {
      hostRegion: '.js-host'
    },

    ui: {
      state: '.js-state'
    },

    /**
     * options.companySecId — the company file this tab belongs to, OR
     * options.customerSecId — the customer file. options.name labels the window
     * when the block comes back empty.
     */
    initialize: function (options) {
      var o = options || {};
      this.companySecId = o.companySecId || null;
      this.customerSecId = o.customerSecId || null;
      this.name = o.name || null;
    },

    onShow: function () {
      var self = this;
      var params = this.scope();
      if (!params) {
        this.ui.state.text('This file has nothing to read from the sales inbox.');
        return;
      }
      App.request('salesInbox:worklist-open', params)
        .done(function (block) {
          if (self.isDestroyed) { return; }
          self.showBlock(block);
        })
        .fail(function () {
          if (self.isDestroyed) { return; }
          self.ui.state.addClass('wl-embed-state-bad')
            .text('Could not load the sales inbox for this file.');
        });
    },

    scope: function () {
      if (this.companySecId) { return { companySecId: this.companySecId }; }
      if (this.customerSecId) { return { customerSecId: this.customerSecId }; }
      return null;
    },

    showBlock: function (block) {
      var self = this;
      var customer = block || this.emptyBlock();
      if (!customer.items) { customer.items = []; }
      if (this.customerSecId) { customer = this.stripCompany(customer); }
      require(['salesInboxWorklistPopupView'], function (PopupView) {
        if (self.isDestroyed) { return; }
        self.ui.state.remove();
        self.bodyView = new PopupView({ customer: customer });
        self.getRegion('hostRegion').show(self.bodyView);
      });
    },

    /**
     * A 204 comes back empty: the file is known, it simply has nothing pending.
     * The window still has a history worth reading, so it opens on a block
     * carrying only the file's identity — without the name it would head itself
     * "Unknown customer" on someone we can name.
     */
    emptyBlock: function () {
      return {
        companySecId: this.companySecId,
        companyName: this.companySecId ? this.name : null,
        customerSecId: this.customerSecId,
        customerName: this.customerSecId ? this.name : null,
        items: []
      };
    },

    /**
     * A customer file is about THAT customer, company or no company. The block
     * the backend assembles still groups them under their company when they
     * have one, and the window would then read the company's name and — worse —
     * send companySecId on its history and closed-work calls, silently widening
     * both to every colleague. So the company is dropped here: what stays is
     * one customer, scoped end to end.
     */
    stripCompany: function (block) {
      var scoped = _.omit(block, 'companySecId', 'companyName');
      if (!scoped.customerSecId) { scoped.customerSecId = this.customerSecId; }
      if (!scoped.customerName) { scoped.customerName = this.name; }
      return scoped;
    }
  });
});

